import { Loading } from "@/components/common/Loading/Loading"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { AlertTriangle } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toastApiError } from "../../api/apiError"
import { useAnalyzeStatus, useRetryAnalysis } from "../../api/importsApi"
import { ENTITY_META, type EntityGroupType } from "../../api/ingestionApi"
import { useSmoothedProgress } from "../../hooks/useSmoothedProgress"
import { isAnalysisFailed } from "../../utils/importStatus"
import { SectionLabel } from "../shared/SectionLabel"
import { EntityScoreboard } from "./EntityScoreboard"
import { ProcessingTicker } from "./ProcessingTicker"
import { RADIAL_PROCESSING_GLOW } from "./masterDataStyles"

const ENTITY_TYPES = Object.keys(ENTITY_META) as EntityGroupType[]
const IMPORT_LIST = "/material-compliance/master-data/import"
/** The page stops waiting after this long, so a stalled worker never spins forever. */
export const ANALYSIS_TIMEOUT_MS = 15 * 60 * 1000

export function MasterDataProcessingModule() {
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const fileIds = searchParams.getAll("file_ids")

  useEffect(() => {
    if (fileIds.length === 0) {
      navigate("/material-compliance/master-data/import", { replace: true })
    }
  }, [fileIds.length, navigate])

  const { data: analyzeStatus, isLoading } = useAnalyzeStatus({
    enabled: fileIds.length > 0,
    fileIds,
  })

  const overall = analyzeStatus?.overall
  const analyzeFiles = useMemo(
    () => analyzeStatus?.files ?? [],
    [analyzeStatus],
  )
  // The analyze endpoint only counts finished files (0 → 100 for a
  // single-file import), so smooth the value for a continuous animation.
  const progressPct = useSmoothedProgress(overall?.progressPct ?? 0)

  // Per-entity counts summed from `summary.entitiesToWrite` across all files
  // that have finished analysis (i.e. have a summary).
  const entityCounts = useMemo(() => {
    const counts = Object.fromEntries(
      ENTITY_TYPES.map((type) => [type, 0]),
    ) as Record<EntityGroupType, number>
    for (const file of analyzeFiles) {
      const entities = file.summary?.entitiesToWrite
      if (!entities) continue
      for (const type of ENTITY_TYPES) counts[type] += entities[type] ?? 0
    }
    return counts
  }, [analyzeFiles])

  // Grand total including relationship tables (product_articles, mappings, …).
  const totalRecords = useMemo(() => {
    let total = 0
    for (const file of analyzeFiles) {
      const entities = file.summary?.entitiesToWrite
      if (!entities) continue
      for (const count of Object.values(entities)) total += count
    }
    return total
  }, [analyzeFiles])

  // Files still queued or actively converting — not ready to redirect yet.
  const conversionInProgress = analyzeFiles.some(
    (f) => f.conversionStatus === "pending" || f.conversionStatus === "parsing",
  )

  const failedFiles = analyzeFiles.filter(isAnalysisFailed)
  const retryAnalysis = useRetryAnalysis()
  const { hasPermission } = useUser()
  const canRetry = hasPermission(PERMISSIONS.MC_IMPORTS_UPLOAD)

  // Moved by every retry, so a retried file gets the full window again.
  const [deadline, setDeadline] = useState(
    () => Date.now() + ANALYSIS_TIMEOUT_MS,
  )
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), deadline - Date.now())
    return () => clearTimeout(timer)
  }, [deadline])

  async function handleRetry() {
    // Before dispatch: a partial success must still give the restarted files a full window.
    setTimedOut(false)
    setDeadline(Date.now() + ANALYSIS_TIMEOUT_MS)
    try {
      await Promise.all(
        failedFiles.map((f) => retryAnalysis.mutateAsync(f.fileId)),
      )
    } catch (err) {
      toastApiError(err, translate, "MC_MD_IMPORT_RETRY_ERROR")
    }
  }

  // No file has produced a summary yet — the scoreboard shows rolling
  // placeholder digits instead of misleading zeros.
  const countsPending = !analyzeFiles.some((f) => f.summary)

  // Once analysis completes, linger for 3s so the user can read the final
  // counts before moving on. Scheduled once via ref — polling keeps producing
  // new `overall` objects, which would otherwise reset an effect-local timer.
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (redirectTimer.current) return
    if (!overall || overall.inFlight > 0 || conversionInProgress) return
    // A failed file would vanish on the mapping page; the user decides below.
    if (failedFiles.length > 0) return
    if (overall.done > 0) {
      const qs = searchParams.toString()
      redirectTimer.current = setTimeout(() => {
        navigate(
          `/material-compliance/master-data/mapping${qs ? `?${qs}` : ""}`,
          { replace: true },
        )
      }, 3000)
    }
  }, [
    overall,
    conversionInProgress,
    failedFiles.length,
    navigate,
    searchParams,
  ])

  useEffect(
    () => () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current)
    },
    [],
  )

  useEffect(() => {
    if (isLoading || !analyzeStatus) return
    if (analyzeStatus.files.length === 0) {
      navigate(IMPORT_LIST, { replace: true })
    }
  }, [isLoading, analyzeStatus, navigate])

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div
          className="pointer-events-none absolute inset-0"
          style={RADIAL_PROCESSING_GLOW}
        />
        <div className="relative">
          <Loading />
        </div>
      </div>
    )
  }

  const stillWorking = (overall?.inFlight ?? 0) > 0 || conversionInProgress
  if (failedFiles.length > 0 && !stillWorking) {
    const analyzedIds = analyzeFiles
      .filter((f) => f.summary)
      .map((f) => ["file_ids", f.fileId])
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div
          role="alert"
          className="flex max-w-xl flex-col items-center gap-4 text-center"
        >
          <AlertTriangle size={28} className="text-red1" />
          <SectionLabel>{translate("MC_MD_PROC_FAILED_TITLE")}</SectionLabel>
          <ul className="space-y-2 text-sm">
            {failedFiles.map((f) => (
              <li key={f.fileId}>
                <span className="text-white">{f.filename}</span>
                <span className="block text-grey5">{f.error}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(IMPORT_LIST)}>
              {translate("MC_MD_PROC_BACK_TO_LIST")}
            </Button>
            {analyzedIds.length > 0 && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    `/material-compliance/master-data/mapping?${new URLSearchParams(analyzedIds)}`,
                  )
                }
              >
                {translate("MC_MD_PROC_CONTINUE_WITH_ANALYZED", {
                  count: analyzedIds.length,
                })}
              </Button>
            )}
            {canRetry && (
              <Button onClick={handleRetry} disabled={retryAnalysis.isPending}>
                {translate("MC_MD_IMPORT_RETRY")}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const analyzed = !stillWorking && (overall?.done ?? 0) > 0
  if (timedOut && !analyzed) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div
          role="alert"
          className="flex max-w-xl flex-col items-center gap-4 text-center"
        >
          <AlertTriangle size={28} className="text-attention" />
          <SectionLabel>{translate("MC_MD_PROC_TIMEOUT_TITLE")}</SectionLabel>
          <p className="text-sm text-grey5">
            {translate("MC_MD_PROC_TIMEOUT_DESC")}
          </p>
          <Button variant="outline" onClick={() => navigate(IMPORT_LIST)}>
            {translate("MC_MD_PROC_BACK_TO_LIST")}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div
        className="pointer-events-none absolute inset-0"
        style={RADIAL_PROCESSING_GLOW}
      />

      <div className="relative flex flex-col items-center gap-8">
        <Loading progressValue={progressPct} />

        <div className="space-y-2 text-center">
          <SectionLabel>
            {translate("MC_MD_PROC_EXTRACTING_LABEL")}
          </SectionLabel>
          <ProcessingTicker converting={conversionInProgress} />
          <p className="">
            {analyzeFiles.length === 1
              ? translate("MC_MD_PROC_READING_ONE")
              : translate("MC_MD_PROC_READING_MANY", {
                  count: analyzeFiles.length,
                })}
            {!countsPending
              ? ` ${translate("MC_MD_PROC_RECORDS_COUNT", { total: totalRecords })}`
              : ""}
          </p>
        </div>

        <EntityScoreboard counts={entityCounts} pending={countsPending} />
      </div>
    </div>
  )
}
